import { api, type DocumentMeta } from '../services/api';
import { FilterBar, type FilterBarConfig, type FilterState } from './FilterBar';
import { UNIFIED_FILTERS, UNIFIED_ACTIONS } from '../config/filterbar-config';

export class ProjectsView {
  private projectName: string = '';
  private documents: DocumentMeta[] = [];
  private filterBar: FilterBar | null = null;
  private currentFilterState: FilterState | null = null;
  private onDocSelect: ((doc: DocumentMeta) => void) | null = null;

  // DOM elements
  private emptyStateEl: HTMLElement | null;
  private detailEl: HTMLElement | null;
  private contentEl: HTMLElement | null;

  constructor() {
    this.emptyStateEl = document.getElementById('projects-empty');
    this.detailEl = document.getElementById('projects-detail');
    this.contentEl = document.querySelector('.projects-content');

    // Always initialize FilterBar
    this.initFilterBar();
  }

  private destroyFilterBar(): void {
    if (this.filterBar) {
      const container = document.getElementById('projects-filter-bar');
      if (container) container.innerHTML = '';
      this.filterBar = null;
    }
  }

  private initFilterBar(): void {
    // Only init if not already done
    if (this.filterBar) return;

    const config: FilterBarConfig = {
      showSearch: true,
      showApplyButton: true,
      filters: UNIFIED_FILTERS,
      showSort: true,
      customActions: UNIFIED_ACTIONS,
      viewContext: 'projects',
      title: this.projectName || 'Projects'
    };

    this.filterBar = new FilterBar('projects-filter-bar', config);

    // Handle apply
    this.filterBar.onApplyCallback((state) => {
      this.currentFilterState = state;
      this.render();
    });

    // Handle search
    this.filterBar.onFilterChangeCallback((state) => {
      this.currentFilterState = state;
    });

    // Handle action buttons
    this.filterBar.onActionCallback((actionId) => {
      this.handleAction(actionId);
    });
  }

  private handleAction(actionId: string): void {
    switch (actionId) {
      case 'add':
        this.createNote();
        break;
      case 'delete':
        // Delete handled at item level
        break;
    }
  }

  private async createNote(): Promise<void> {
    try {
      const response = await api.createDocumentWithUniqueTitle({
        title: 'New Note',
        content: ''
      });

      if (response.success && response.data) {
        const updateData: Record<string, string> = { type: 'Notes' };
        if (this.projectName) {
          updateData.project = this.projectName;
        }
        await api.updateDocument(response.data.meta.id, updateData);

        if (this.onDocSelect) {
          this.onDocSelect({
            ...response.data.meta,
            project: this.projectName || undefined,
            type: 'Notes'
          });
        }
      }
    } catch (error) {
      console.error('Error creating note:', error);
    }
  }

  private async _createResource(): Promise<void> {
    try {
      const response = await api.createDocumentWithUniqueTitle({
        title: 'New Resource',
        content: ''
      });

      if (response.success && response.data) {
        const updateData: Record<string, string> = { type: 'Resource' };
        if (this.projectName) {
          updateData.project = this.projectName;
        }
        await api.updateDocument(response.data.meta.id, updateData);

        if (this.onDocSelect) {
          this.onDocSelect({
            ...response.data.meta,
            project: this.projectName || undefined,
            type: 'Resource'
          });
        }
      }
    } catch (error) {
      console.error('Error creating resource:', error);
    }
  }

  // Show project with filtered documents
  show(projectName: string, allDocuments: DocumentMeta[]): void {
    this.projectName = projectName;
    this.documents = allDocuments.filter(d => d.project === projectName);

    // Show detail, hide empty state
    if (this.emptyStateEl) this.emptyStateEl.classList.add('hidden');
    if (this.detailEl) this.detailEl.classList.remove('hidden');

    // Initialize or reinit FilterBar with new project name
    this.destroyFilterBar();
    this.initFilterBar();

    // Update tags filter options
    const allTags = this.getProjectTags();
    if (this.filterBar) {
      this.filterBar.setFilterOptions('tags', allTags.map(t => ({ value: t, label: t })));
    }

    this.render();
  }

  // Reset to empty state
  reset(): void {
    this.projectName = '';
    this.documents = [];

    // Show empty state, hide detail
    if (this.emptyStateEl) this.emptyStateEl.classList.remove('hidden');
    if (this.detailEl) this.detailEl.classList.add('hidden');
  }

  // Get filtered and sorted documents
  private getFilteredDocuments(): DocumentMeta[] {
    let filtered = [...this.documents];
    const state = this.currentFilterState;

    if (!state) return filtered;

    // Filter by type
    const types = state.activeFilters.get('type');
    if (types && types.size > 0) {
      filtered = filtered.filter(doc => {
        const docType = doc.type || 'Notes';
        return types.has(docType);
      });
    }

    // Filter by category
    const categories = state.activeFilters.get('category');
    if (categories && categories.size > 0) {
      filtered = filtered.filter(doc => doc.resourceCategory && categories.has(doc.resourceCategory));
    }

    // Filter by tags
    const tags = state.activeFilters.get('tags');
    if (tags && tags.size > 0) {
      filtered = filtered.filter(doc => {
        return doc.tags?.some(tag => tags.has(tag));
      });
    }

    // Filter by search
    if (state.search) {
      const query = state.search.toLowerCase();
      filtered = filtered.filter(doc => {
        const title = doc.title?.toLowerCase() || '';
        const summary = ((doc.customFields as Record<string, string>)?.summary || '').toLowerCase();
        const docTags = doc.tags?.join(' ').toLowerCase() || '';
        return title.includes(query) || summary.includes(query) || docTags.includes(query);
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (state.sortField === 'title') {
        aVal = a.title?.toLowerCase() || '';
        bVal = b.title?.toLowerCase() || '';
      } else {
        aVal = a[state.sortField as 'updated_at' | 'created_at'] || 0;
        bVal = b[state.sortField as 'updated_at' | 'created_at'] || 0;
      }

      if (state.sortDirection === 'desc') {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      } else {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
    });

    return filtered;
  }

  // Separate documents by type field
  private getNotes(docs: DocumentMeta[]): DocumentMeta[] {
    return docs.filter(d => d.type === 'Notes' || !d.type);
  }

  private getResources(docs: DocumentMeta[]): DocumentMeta[] {
    return docs.filter(d => d.type === 'Resource');
  }

  // Get combined tags from all documents in project
  private getProjectTags(): string[] {
    const tags = new Set<string>();
    for (const doc of this.documents) {
      for (const tag of doc.tags || []) {
        tags.add(tag);
      }
    }
    return Array.from(tags).sort();
  }

  // Format relative time
  private formatTime(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;

    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Get icon class based on document properties
  private getIconInfo(doc: DocumentMeta): { icon: string; class: string } {
    const title = doc.title?.toLowerCase() || '';
    const customFields = doc.customFields as Record<string, unknown> | undefined;
    const url = customFields?.url as string | undefined;

    if (title.endsWith('.pdf')) {
      return { icon: 'picture_as_pdf', class: 'icon-pdf' };
    }
    if (url) {
      return { icon: 'link', class: 'icon-link' };
    }
    if (title.includes('sheet') || title.includes('spreadsheet') || title.endsWith('.xlsx') || title.endsWith('.csv')) {
      return { icon: 'table_chart', class: 'icon-sheet' };
    }
    if (title.endsWith('.png') || title.endsWith('.jpg') || title.endsWith('.jpeg') || title.endsWith('.gif')) {
      return { icon: 'image', class: 'icon-image' };
    }
    return { icon: 'description', class: 'icon-note' };
  }

  // Render all
  private render(): void {
    if (!this.contentEl) return;

    const filteredDocs = this.getFilteredDocuments();
    const notes = this.getNotes(filteredDocs);
    const resources = this.getResources(filteredDocs);
    const tags = this.getProjectTags().slice(0, 5);

    this.contentEl.innerHTML = `
      <div class="projects-title-row">
        <h1 id="project-title">${this.escapeHtml(this.projectName)}</h1>
        <div id="project-tags" class="project-tags">
          ${tags.map(tag => `<span class="project-tag">#${this.escapeHtml(tag)}</span>`).join('')}
        </div>
      </div>
      <div class="projects-columns">
        <section class="project-section">
          <div class="project-section-header">
            <h2>Associated Notes</h2>
            <span class="section-count">${notes.length} note${notes.length !== 1 ? 's' : ''}</span>
          </div>
          <ul class="project-list" id="project-notes-list">
            ${notes.length > 0 ? notes.map(doc => this.renderNoteItem(doc)).join('') : '<li class="project-item-empty">No notes yet</li>'}
          </ul>
          <button class="add-item-btn" id="btn-add-note">
            <span class="material-symbols-outlined">add</span>
            Add new note
          </button>
        </section>
        <section class="project-section">
          <div class="project-section-header">
            <h2>Associated Resources</h2>
            <span class="section-count">${resources.length} item${resources.length !== 1 ? 's' : ''}</span>
          </div>
          <ul class="project-list" id="project-resources-list">
            ${resources.length > 0 ? resources.map(doc => this.renderResourceItem(doc)).join('') : '<li class="project-item-empty">No resources yet</li>'}
          </ul>
          <button class="add-item-btn" id="btn-add-resource">
            <span class="material-symbols-outlined">add</span>
            Add new resource
          </button>
        </section>
      </div>
    `;

    // Attach click handlers
    this.attachClickHandlers();
  }

  private renderNoteItem(doc: DocumentMeta): string {
    const customFields = doc.customFields as Record<string, string> | undefined;
    const summary = customFields?.summary || '';

    return `
      <li class="project-item" data-id="${doc.id}">
        <div class="project-item-icon icon-note">
          <span class="material-symbols-outlined">description</span>
        </div>
        <div class="project-item-content">
          <h3 class="project-item-title">${this.escapeHtml(doc.title || 'Untitled')}</h3>
          ${summary ? `<p class="project-item-desc">${this.escapeHtml(summary.slice(0, 80))}</p>` : ''}
        </div>
        <span class="project-item-time">${this.formatTime(doc.updated_at)}</span>
      </li>
    `;
  }

  private renderResourceItem(doc: DocumentMeta): string {
    const iconInfo = this.getIconInfo(doc);
    const customFields = doc.customFields as Record<string, string> | undefined;
    const url = customFields?.url;
    const badge = doc.resourceCategory || '';

    return `
      <li class="project-item" data-id="${doc.id}" ${url ? `data-url="${this.escapeHtml(url)}"` : ''}>
        <div class="project-item-icon ${iconInfo.class}">
          <span class="material-symbols-outlined">${iconInfo.icon}</span>
        </div>
        <div class="project-item-content">
          <h3 class="project-item-title">${this.escapeHtml(doc.title || 'Untitled')}</h3>
          ${badge ? `<span class="project-item-badge">${this.escapeHtml(badge)}</span>` : ''}
        </div>
        ${url ? '<span class="project-item-external"><span class="material-symbols-outlined">open_in_new</span></span>' : ''}
      </li>
    `;
  }

  private attachClickHandlers(): void {
    // Note items
    document.querySelectorAll('#project-notes-list .project-item').forEach(item => {
      item.addEventListener('click', () => {
        const docId = (item as HTMLElement).dataset.id;
        const doc = this.documents.find(d => d.id === docId);
        if (doc && this.onDocSelect) {
          this.onDocSelect(doc);
        }
      });
    });

    // Resource items
    document.querySelectorAll('#project-resources-list .project-item').forEach(item => {
      item.addEventListener('click', () => {
        const url = (item as HTMLElement).dataset.url;
        if (url) {
          window.open(url, '_blank');
        } else {
          const docId = (item as HTMLElement).dataset.id;
          const doc = this.documents.find(d => d.id === docId);
          if (doc && this.onDocSelect) {
            this.onDocSelect(doc);
          }
        }
      });
    });

    // Add buttons
    document.getElementById('btn-add-note')?.addEventListener('click', () => {
      this.createNote();
    });

    document.getElementById('btn-add-resource')?.addEventListener('click', () => {
      this._createResource();
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Callback for document selection
  onDocumentSelect(callback: (doc: DocumentMeta) => void): void {
    this.onDocSelect = callback;
  }
}
